import Comparison from "@components/Dashboard/Comparison";
import Consolidation from "@components/Dashboard/Consolidation";
import { Typography } from "@hellocarbo/lasagne";
import { useTranslation } from "react-i18next";
import { data, LoaderFunctionArgs, useLoaderData } from "react-router";

import OnboardingActionsSection from "@/components/Onboarding/OnboardingActionsSection";
import OnboardingResourcesSection from "@/components/Onboarding/OnboardingResourcesSection";
import PageWrapper from "@/components/ui/PageWrapper";
import type {
  Comparison as TComparison,
  ConsolidatedReview as TConsolidatedReview,
} from "@/types";

export async function loader({ request }: LoaderFunctionArgs) {
  const consolidatedReviewResponse = await fetch(
    "/api/client/reviews/consolidated"
  );

  if (consolidatedReviewResponse.status !== 200) {
    throw data("Failed to load consolidated review", {
      status: consolidatedReviewResponse.status,
    });
  }

  const { data: consolidatedReview } =
    (await consolidatedReviewResponse.json()) as {
      data: TConsolidatedReview;
    };

  if (request.url.includes("/consolidation")) {
    return {
      consolidatedReview,
    };
  }

  const comparisonResponse = await fetch("/api/client/dashboard/comparison/");

  if (comparisonResponse.status !== 200) {
    throw data("Failed to load comparison", {
      status: comparisonResponse.status,
    });
  }

  const { data: comparisonData } = (await comparisonResponse.json()) as {
    data: TComparison;
  };

  return {
    consolidatedReview,
    comparisonData,
  };
}

function Dashboard({ showComparison = true }: { showComparison: boolean }) {
  const { t } = useTranslation("common");

  const { consolidatedReview, comparisonData } = useLoaderData<typeof loader>();

  return (
    <PageWrapper>
      <div className="mx-xl my-lg">
        <Typography step="3" as="h1" weight="bold" className="mb-sm">
          {t("common:welcome")}
        </Typography>
        <div className="gap-y-xl flex flex-col">
          <OnboardingActionsSection />
          <Consolidation consolidatedReview={consolidatedReview} />
          {showComparison && comparisonData ? (
            <Comparison comparisonData={comparisonData} />
          ) : null}
          <OnboardingResourcesSection />
        </div>
      </div>
    </PageWrapper>
  );
}

export default Dashboard;
